import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import notificationService from '../services/api/notificationService';
import { useAuthStore } from '../stores/authStore';

const NOTIFICATIONS_KEY = ['notifications'] as const;
const UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const;

/** Both notification queries poll on the same cadence — see useNotifications. */
const POLL_INTERVAL_MS = 15_000;

/**
 * The notification list backing the bell dropdown.
 *
 * This polls on the SAME interval as the unread count. The two must stay in
 * step: the bell is permanently mounted, so without an interval this query
 * never refetches on its own (nothing remounts it, and a dropdown opening is
 * not a fetch trigger) — the badge would tick up from the polled count while
 * the dropdown underneath it still rendered the list from page load, i.e. a
 * bell showing "3" over an empty "No notifications" panel.
 */
export function useNotifications(unreadOnly?: boolean) {
  const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);

  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, { unreadOnly }],
    queryFn: () =>
      notificationService.getNotifications(unreadOnly ? { unreadOnly: true } : undefined),
    refetchInterval: isReady ? POLL_INTERVAL_MS : false,
    staleTime: 10_000,
    enabled: isReady,
    retry: false,
  });
}

export function useUnreadCount() {
  const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);

  return useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: isReady ? POLL_INTERVAL_MS : false,
    staleTime: 10_000,
    enabled: isReady,
    retry: false,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.dismissNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}
